/**
 * What differs in a mirror folder from its last sync (HEAD), as Apiboo items.
 * Shared by the Git page's Changes (gitChanges.js) and the sync itself
 * (workspaceGitSync.pushWorkspace — the readable commit message, set 7d).
 * Reads only; the caller brings the mirror up to date first.
 */
import { invoke } from '@tauri-apps/api/core';
import { readTextSafe } from '../gitFileWriter.js';
import { isMediaPath } from './scopeRepoLayout';
import { describeChanges, summarizeChanges } from './changeItems';

/** More than this many files is a bulk change: listed, not read one by one. */
const MAX_READ = 300;

/** @returns {Promise<{ items: Array, files: Array }>} */
/** `layout`: 'workspace' (default) or 'scope' (git sync Stage 2) — how files are named. */
export async function readMirrorChanges(dir, layout = 'workspace') {
  const changed = (await invoke('git_changes', { repoPath: dir })) || [];
  const fs = await import('@tauri-apps/plugin-fs');
  const files = [];
  for (const [i, f] of changed.entries()) {
    // Images in a scope repository are listed, never read as text.
    const read = i < MAX_READ && !(layout === 'scope' && isMediaPath(f.path));
    const before = read && f.kind !== 'added' ? await invoke('git_file_at_head', { repoPath: dir, path: f.path }) : null;
    const after = read && f.kind !== 'deleted' ? await readTextSafe(fs, `${dir}/${f.path}`) : null;
    files.push({ ...f, before: before ?? null, after: after ?? null });
  }
  return { items: describeChanges(files, layout), files };
}

/**
 * The commit message for what is about to be synced: "2 requests, 1
 * environment" (owner plan: "readable commit messages"). Never fails a sync —
 * anything unreadable falls back to `fallback`.
 */
export async function commitMessageFor(dir, fallback, layout = 'workspace') {
  try {
    const { items } = await readMirrorChanges(dir, layout);
    return summarizeChanges(items) || fallback;
  } catch {
    return fallback;
  }
}
